import firstPartyAnalytics from './firstPartyAnalytics';
import { AnalyticsEvents, hostOf } from './analyticsEvents';

/**
 * Slider-CMS-Tracking (Marcel 2026-09-28) – Vertrag mit Backend (analytics-event-catalog.js):
 *   slider_impression / slider_click mit slider_location, slider_id, slide_id, slide_title,
 *   slide_type (manual|sponsor) und link_host (nur bei Klick, Host der Ziel-URL).
 *
 * - Impression EINMAL je Sitzung, Location und Slide (sessionStorage-Set, fail-soft: ohne
 *   Storage nur im Speicher), Klick jedes Mal.
 * - Admins werden NIE getrackt, Vorschau nie (Aufrufer übergibt isAdmin/isPreview).
 * - Sender injizierbar (Tests), nie werfen.
 */
export interface SliderRef {
  location: string | null | undefined;
  sliderId: string | null | undefined;
  slideId: string | null | undefined;
  slideTitle?: string | null;
  slideType?: 'manual' | 'sponsor' | string | null;
}

export interface SliderTrackingContext {
  /** Admins werden nie getrackt */
  isAdmin?: boolean;
  /** Vorschau (Admin-Formular) wird nie getrackt */
  isPreview?: boolean;
}

type Sender = (name: string, params: Record<string, unknown>) => unknown;

/** sessionStorage-ähnlicher Speicher (injizierbar für Tests) */
export interface KeyStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const SLIDER_IMPRESSIONS_KEY = 'dc-slider-impressions';

const defaultStore = (): KeyStore | null => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

/** PURE: Dedup-Schlüssel je Location + Slide */
export const impressionKey = (ref: SliderRef): string => `${ref.location ?? ''}|${ref.slideId ?? ''}`;

/** PURE: Event-Parameter laut Vertrag */
export const sliderParams = (ref: SliderRef, linkUrl?: string | null): Record<string, unknown> => ({
  slider_location: ref.location ?? null,
  slider_id: ref.sliderId ?? null,
  slide_id: ref.slideId ?? null,
  slide_title: ref.slideTitle ?? null,
  slide_type: ref.slideType === 'sponsor' ? 'sponsor' : 'manual',
  ...(linkUrl !== undefined ? { link_host: linkUrl ? hostOf(linkUrl) : null } : {}),
});

const complete = (ref: SliderRef): boolean => Boolean(ref?.location && ref?.sliderId && ref?.slideId);
const blocked = (ctx?: SliderTrackingContext): boolean => Boolean(ctx?.isAdmin || ctx?.isPreview);

export const createSliderTracking = (
  send: Sender = (n, p) => firstPartyAnalytics.trackEvent(n, p),
  store: KeyStore | null | undefined = undefined,
) => {
  const memory = new Set<string>();
  const getStore = (): KeyStore | null => (store === undefined ? defaultStore() : store);

  const readSeen = (): Set<string> => {
    const s = getStore();
    if (!s) return memory;
    try {
      const raw = s.getItem(SLIDER_IMPRESSIONS_KEY);
      const list = raw ? JSON.parse(raw) : [];
      const stored: string[] = Array.isArray(list) ? list.filter((x) => typeof x === 'string') : [];
      return new Set<string>(Array.from(memory).concat(stored));
    } catch {
      return memory;
    }
  };
  const persist = (seen: Set<string>) => {
    const s = getStore();
    if (!s) return;
    try {
      s.setItem(SLIDER_IMPRESSIONS_KEY, JSON.stringify(Array.from(seen)));
    } catch {
      /* Storage gesperrt/voll – Speicher-Set reicht */
    }
  };

  return {
    /** true = gesendet (erstes Mal in dieser Sitzung), false = übersprungen */
    impression(ref: SliderRef, ctx?: SliderTrackingContext): boolean {
      if (blocked(ctx) || !complete(ref)) return false;
      const key = impressionKey(ref);
      const seen = readSeen();
      if (seen.has(key)) return false;
      seen.add(key);
      memory.add(key);
      persist(seen);
      try { void send(AnalyticsEvents.sliderImpression, sliderParams(ref)); } catch { /* nie werfen */ }
      return true;
    },
    /** Klick auf einen Slide/Sponsor-Link – jedes Mal, mit Host der Ziel-URL */
    click(ref: SliderRef, linkUrl: string | null | undefined, ctx?: SliderTrackingContext): boolean {
      if (blocked(ctx) || !complete(ref)) return false;
      try { void send(AnalyticsEvents.sliderClick, sliderParams(ref, linkUrl ?? null)); } catch { /* nie werfen */ }
      return true;
    },
    /** Dedup zurücksetzen (Tests) */
    reset(): void {
      memory.clear();
      persist(new Set());
    },
  };
};

const sliderTracking = createSliderTracking();
export default sliderTracking;
